import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { categories } from "@/mocks/categories";

export default function SiteHeader() {
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const catRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (catRef.current && !catRef.current.contains(e.target as Node)) {
        setCatOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : "/search");
    setMobileOpen(false);
  };

  const navClass = ({ isActive }: { isActive: boolean }) =>
    `px-3 py-2 text-sm font-label rounded-md transition-colors duration-200 ${
      isActive
        ? "text-primary-600"
        : "text-foreground-700 hover:text-primary-600"
    }`;

  return (
    <header className="sticky top-0 z-40 w-full border-b border-background-200 bg-background-50/95 backdrop-blur">
      <div className="flex h-16 items-center gap-4 px-4 md:px-6">
        <Link to="/" className="flex items-center gap-2 shrink-0">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-play-fill text-lg"></i>
          </span>
          <span className="font-heading text-xl font-extrabold tracking-tight text-foreground-950">
            日批视频
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1 ml-2">
          <NavLink to="/" end className={navClass}>
            首页
          </NavLink>
          <div className="relative" ref={catRef}>
            <button
              type="button"
              onClick={() => setCatOpen((v) => !v)}
              className="flex items-center gap-1 px-3 py-2 text-sm font-label rounded-md text-foreground-700 hover:text-primary-600 transition-colors duration-200 cursor-pointer"
            >
              分类
              <i
                className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${
                  catOpen ? "rotate-180" : ""
                }`}
              ></i>
            </button>
            {catOpen && (
              <div className="absolute left-0 top-full mt-2 w-52 rounded-lg border border-background-200 bg-background-50 p-2 animate-fade-in">
                {categories.map((c) => (
                  <Link
                    key={c.slug}
                    to={`/category/${c.slug}`}
                    onClick={() => setCatOpen(false)}
                    className="flex items-center gap-3 rounded-md px-3 py-2 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-600 transition-colors duration-200"
                  >
                    <i className={`${c.icon} text-lg text-primary-500`}></i>
                    {c.name}
                  </Link>
                ))}
              </div>
            )}
          </div>
          <NavLink to="/search" className={navClass}>
            搜索
          </NavLink>
          <NavLink to="/about" className={navClass}>
            关于
          </NavLink>
        </nav>

        <form
          onSubmit={submitSearch}
          className="ml-auto hidden md:flex items-center gap-2 rounded-full border border-background-200 bg-background-100 px-3 py-1.5 w-64 lg:w-80 focus-within:border-primary-400 transition-colors duration-200"
        >
          <i className="ri-search-line text-base text-foreground-500"></i>
          <input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="搜索视频、分类或关键词"
            className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 outline-none"
          />
          <button
            type="submit"
            className="shrink-0 rounded-full bg-primary-500 px-3 py-1 text-xs font-label text-background-50 hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
          >
            搜索
          </button>
        </form>

        <button
          type="button"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label="菜单"
          className="ml-auto md:hidden flex h-10 w-10 items-center justify-center rounded-md text-foreground-900 cursor-pointer"
        >
          <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-2xl`}></i>
        </button>
      </div>

      {mobileOpen && (
        <div className="md:hidden border-t border-background-200 bg-background-50 px-4 py-4 animate-fade-in">
          <form onSubmit={submitSearch} className="mb-4 flex items-center gap-2 rounded-full border border-background-200 bg-background-100 px-3 py-2">
            <i className="ri-search-line text-base text-foreground-500"></i>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索视频、分类或关键词"
              className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 outline-none"
            />
            <button type="submit" className="shrink-0 text-primary-600 text-sm font-label cursor-pointer whitespace-nowrap">
              搜索
            </button>
          </form>
          <nav className="flex flex-col">
            <Link to="/" onClick={() => setMobileOpen(false)} className="rounded-md px-3 py-3 text-sm text-foreground-800 hover:bg-background-100">
              首页
            </Link>
            <span className="px-3 pt-2 pb-1 text-xs font-label uppercase text-foreground-400">分类</span>
            {categories.map((c) => (
              <Link key={c.slug} to={`/category/${c.slug}`} onClick={() => setMobileOpen(false)} className="flex items-center gap-3 rounded-md px-3 py-3 text-sm text-foreground-800 hover:bg-background-100">
                <i className={`${c.icon} text-lg text-primary-500`}></i>
                {c.name}
              </Link>
            ))}
            <Link to="/search" onClick={() => setMobileOpen(false)} className="rounded-md px-3 py-3 text-sm text-foreground-800 hover:bg-background-100">
              搜索
            </Link>
            <Link to="/about" onClick={() => setMobileOpen(false)} className="rounded-md px-3 py-3 text-sm text-foreground-800 hover:bg-background-100">
              关于
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}